import immutableStore from 'utils/immutableStore';
import alt from 'components/Dispatcher';
import ToastStore from '@store/toast/ToastStore';
import {List, Map, Record} from 'immutable';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import CoachingReviewFileUpload from 'records/CoachingReviewFileUpload';
import {ContentDetailId} from 'records/CoachingContentDetail';
import {Store} from '@store/@types/alt';
import {ReviewId} from 'records/ReviewId';

class CoachingReviewStoreState extends Record({
  // Map <ReviewId, List<ReviewFileUpload>>
  reviewFileUploads: Map(),
  highLevelScores: Map(), // Map<ReviewId, {contentDetailId, score: Int(1..100)}>
  // save a copy in case saving fails so it can be rolled back
  reviewFileUploadsBackup: Map(),
  isReviewFeedbackLoading: Map(),
  wasErrorLoadingReviewFeedback: Map(),
  isReviewFeedbackSaving: Map(),
  coachingReviewAnswers: Map(),

  // This flag is used with the AddCallsDialog to handle a redirect
  // back to the review screen when the user is linked to the calls tab.
  reviewRedirect: false,

  // marker to set the current (viewing or actively filling out)
  // review - needed to let upcoming agenda communicate with the coaching page
  currentlyFillingOutReviewId: undefined,

  // A map of live coaching call ids (scorecard model ids) to review ids
  // used to update coaching stats when the live coaching scorecards are
  // updated.
  viewedScorecardsToReviews: Map(),
}) {
  readonly reviewFileUploads: Map<ReviewId, List<CoachingReviewFileUpload>>;
  readonly highLevelScores: Map<ReviewId, {contentDetailId: ContentDetailId; score: number}>;
  readonly reviewFileUploadsBackup: Map<ReviewId, List<CoachingReviewFileUpload>>;

  readonly isReviewFeedbackLoading: Map<ReviewId, boolean>;
  readonly wasErrorLoadingReviewFeedback: Map<ReviewId, boolean>;
  readonly isReviewFeedbackSaving: Map<ReviewId, boolean>;
  readonly coachingReviewAnswers: Map<any, any>;

  readonly reviewRedirect: boolean;
  readonly currentlyFillingOutReviewId?: ReviewId;

  readonly viewedScorecardsToReviews: Map<string, ReviewId>;
}

@immutableStore
class CoachingReviewStore extends Store<CoachingReviewStoreState> {
  constructor() {
    super();
    this.state = new CoachingReviewStoreState();
    this.bindActions(CoachingReviewActions);
  }

  setReviewRedirect(redirect) {
    this.setState(this.state.set('reviewRedirect', redirect));
  }

  fetchReviewFeedback(reviewId) {
    this.setState(
      this.state
        .setIn(['isReviewFeedbackLoading', reviewId], true)
        .setIn(['wasErrorLoadingReviewFeedback', reviewId], false)
    );
  }

  fetchReviewFeedbackSuccess([
    reviewId,
    {reviewFileUploads, coachingReviewAnswers, highLevelScores},
  ]) {
    this.setState(
      this.state
        .setIn(['reviewFileUploads', reviewId], reviewFileUploads)
        .setIn(['highLevelScores', reviewId], highLevelScores)
        .setIn(['coachingReviewAnswers', reviewId], coachingReviewAnswers)
        .setIn(['isReviewFeedbackLoading', reviewId], false)
    );
  }

  fetchReviewFeedbackError(reviewId) {
    this.setState(
      this.state
        .setIn(['isReviewFeedbackLoading', reviewId], false)
        .setIn(['wasErrorLoadingReviewFeedback', reviewId], true)
    );
  }

  updateReviewFileUploads([reviewId, reviewFileUploads]) {
    this.setState(
      this.state
        .setIn(['reviewFileUploadsBackup', reviewId], this.state.reviewFileUploads.get(reviewId))
        .setIn(['isReviewFeedbackSaving', reviewId], true)
    );
  }

  updateReviewFileUploadsSuccess([reviewId, reviewFileUploads]) {
    this.setState(
      this.state
        .setIn(['isReviewFeedbackSaving', reviewId], true)
        .setIn(['reviewFileUploads', reviewId], reviewFileUploads)
    );
  }

  updateReviewFileUploadsError([reviewId, reviewFileUploads]) {
    ToastStore.addToast({
      type: 'error',
      message: 'There was a problem saving the call reviews.',
    });
    this.setState(
      this.state.setIn(
        ['reviewFileUploads', reviewId],
        this.state.reviewFileUploadsBackup.get(reviewId)
      )
    );
  }

  deleteReviewFileUpload(reviewId) {
    this.setState(this.state.setIn(['isReviewFeedbackSaving', reviewId], true));
  }

  deleteReviewFileUploadSuccess([reviewId, reviewFileUploads]) {
    this.setState(
      this.state
        .setIn(['isReviewFeedbackSaving', reviewId], false)
        .setIn(['reviewFileUploads', reviewId], reviewFileUploads)
    );
  }

  deleteReviewFileUploadError([reviewId, error]) {
    console.error(error);
    ToastStore.addToast({
      type: 'error',
      message: 'There was a problem updating the review.',
    });
    this.setState(this.state.setIn(['isReviewFeedbackSaving', reviewId], false));
  }

  saveForLater([review]) {
    this.setState(this.state.setIn(['isReviewFeedbackSaving', review.id], true));
  }

  saveForLaterSuccess([review, reviewFileUploads]) {
    this.setState(
      this.state
        .setIn(['isReviewFeedbackSaving', review.id], false)
        .setIn(['reviewFileUploads', review.id], reviewFileUploads)
    );
  }

  saveForLaterError([review, error]) {
    console.error(error);
    ToastStore.addToast({
      type: 'error',
      message: 'There was a problem updating the review.',
    });
    this.setState(this.state.setIn(['isReviewFeedbackSaving', review.id], false));
  }

  changeFileReviewSkills([reviewId]) {
    this.setState(this.state.setIn(['isReviewFeedbackSaving', reviewId], true));
  }

  changeFileReviewSkillsSuccess([reviewId, reviewFileUploads]) {
    this.setState(
      this.state
        .setIn(['isReviewFeedbackSaving', reviewId], false)
        .setIn(['reviewFileUploads', reviewId], reviewFileUploads)
    );
  }

  changeFileReviewSkillsError([reviewId, error]) {
    console.error(error);
    ToastStore.addToast({
      type: 'error',
      message: 'There was a problem updating the review.',
    });
    this.setState(this.state.setIn(['isReviewFeedbackSaving', reviewId], false));
  }

  startReview(reviewId) {
    this.setState(this.state.set('currentlyFillingOutReviewId', reviewId));
  }

  prepReview(reviewId) {
    this.setState(this.state.set('currentlyFillingOutReviewId', reviewId));
  }

  submitFeedback(review) {
    this.setState(
      this.state
        .setIn(['reviewFileUploadsBackup', review.id], this.state.reviewFileUploads.get(review.id))
        .setIn(['isReviewFeedbackSaving', review.id], true)
    );
  }

  submitFeedbackSuccess([review, {reviewFileUploads, coachingReviewAnswers}]) {
    this.setState(
      this.state
        .setIn(['isReviewFeedbackSaving', review.id], true)
        .setIn(['reviewFileUploads', review.id], reviewFileUploads)
        .setIn(['coachingReviewAnswers', review.id], coachingReviewAnswers)
        .delete('currentlyFillingOutReviewId')
    );
  }

  submitFeedbackError([review, error]) {
    console.error(error);
    ToastStore.addToast({
      type: 'error',
      message: 'There was a problem saving the call reviews.',
    });
    this.setState(
      this.state
        .setIn(['reviewFileUploads', review.id], this.state.reviewFileUploadsBackup.get(review.id))
        .delete('currentlyFillingOutReviewId')
    );
  }

  clearCurrentlyFillingOutReview() {
    this.setState(this.state.delete('currentlyFillingOutReviewId'));
  }

  setCurrentlyFillingOutReview(reviewId) {
    this.setState(this.state.set('currentlyFillingOutReviewId', parseInt(reviewId, 10)));
  }

  setViewingScorecardForReview([callId, reviewId]) {
    this.setState(this.state.setIn(['viewedScorecardsToReviews', callId], reviewId));
  }
}

export default alt.createStore(CoachingReviewStore);
